import { colors } from '@trevor/ui-components';
import { OTHER_LABEL } from '@/components/ChartHolder.constants';
import ChartHolder from '../../components/ChartHolder';
import {
  CONFIG_LOGARITHMIC,
  CONFIG_FLIP_DIMS,
  CONFIG_COLOR,
  CONFIG_REVERSE_X_AXIS,
  CONFIG_FILL_MISSING_VALUES,
  CONFIG_CUMULATIVE,
  CONFIG_2ND_Y_AXIS,
  CONFIG_SHOW_Y_AXIS_LABEL,
  CONFIG_Y_AXIS_MAX,
  CONFIG_Y_AXIS_MIN,
  CONFIG_SHOW_PERCENT,
  CONFIG_COLUMN_NAME,
  CONFIG_MAX_LEGEND_ITEMS,
  CONFIG_Y_AXIS_LABEL,
  CONFIG_2ND_Y_AXIS_LABEL,
  CONFIG_2ND_Y_AXIS_MIN,
  CONFIG_2ND_Y_AXIS_MAX,
  CONFIG_COMPARE_PERIODS,
} from '../../components/charting/chartingConfiguration.constants';
import {
  MONTH_YEAR_OPTION,
  JUST_YEAR_OPTION,
  ISO_DATE_OPTION,
} from '../../components/ChartFormattingOptions';
import { DAY, MONTH } from '../enums/EnumDateTimeAggregation';
import { cellFormatter } from './chart-formatting-helpers';
import ColumnTypeData, { INT, DATE, FLOAT, DATETIME } from '../ColumnType';
import DataTable from '../DataTable';
import DataTableRow from '../DataTableRow';
import DataTableCell from '../DataTableCell';
import DataTableHeading from '../DataTableHeading';
import ColumnName from '../ColumnName';
import {
  BAR,
  BARLINE,
  LINE,
  PIE,
} from '../../components/charting/chartType.constants';

const DEFAULT_RGBA_ALPHA = 0.8;

const hexToRgbVals = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `${r}, ${g}, ${b}`;
};

const baseColours = [
  colors.interface.secondary.normal,
  colors.brand.orange1.normal,
  colors.interface.primary.normal,
  colors.brand.blue1.normal,
  colors.brand.green1.normal,
  colors.brand.purple1.normal,
  colors.feedback.error.normal,
  colors.brand.purple2.normal,
  colors.interface.layout.darker,
  colors.interface.content.normal,
  colors.brand.green2.normal,
  colors.brand.blue2.normal,
  colors.brand.red2.lighter,
  colors.brand.blue1.darkest,
  colors.interface.secondary.lightest,
  colors.feedback.warning.lightest,
  colors.interface.primary.lightest,
  colors.brand.blue1.lightest,
  colors.brand.green1.lightest,
  colors.brand.purple1.lightest,
  colors.feedback.error.lightest,
  colors.brand.purple2.lightest,
  colors.interface.layout.normal,
  colors.interface.content.lightest,
  colors.brand.green2.lightest,
  colors.brand.blue2.lightest,
  colors.brand.red2.lightest,
  colors.brand.blue1.darker,
].map(hexToRgbVals);

function componentToHex(c) {
  const hex = parseInt(c, 10).toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}

//
// https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
//
export function rgbToHex(r, g, b) {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

//
// https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
//
export function hexToRgb(hex, alpha = DEFAULT_RGBA_ALPHA) {
  if (!hex) {
    return null;
  }
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const BASE_COLORS_HEX = baseColours
  .map((c) => c.split(','))
  .map((split) => rgbToHex(split[0].trim(), split[1].trim(), split[2].trim()));

export const NICE_COLORS = BASE_COLORS_HEX.slice(0, 40);

export function buildDatasets(
  dataTable,
  presentation,
  dimensions,
  getChartOption,
) {
  let xAxis = [];
  const usedXs = {};
  const oneD = {};
  const twoD = {};
  const zValuesByX = {}; // maintains order of zValues

  const updatedDataTable = collapseLongTailIfNecessary(
    dataTable,
    dimensions,
    presentation,
    getChartOption,
  );

  const isCumulative =
    getChartOption(CONFIG_CUMULATIVE) &&
    [BAR, LINE].includes(presentation) &&
    updatedDataTable.headings().length === 2 &&
    [DATE, DATETIME].includes(
      updatedDataTable.headings()[0].columnType().type(),
    );

  const rowLength = updatedDataTable.headings().length;
  let maxZ = 0;
  let maxCount = 0;

  // for cumulative totals, sort the rows by the first column (the date) in ascending order (otherwise the running total doesn't make sense)
  const dtRows = !isCumulative
    ? updatedDataTable.rows().slice()
    : updatedDataTable
        .rows()
        .slice()
        .sort((a, b) => {
          if (b.cells()[0].value() < a.cells()[0].value()) {
            return -1;
          }
          return 1;
        })
        .map((row, i) => new DataTableRow(i, row.cells()));

  dtRows.forEach((row) => {
    const x = row.cells()[0].value();
    const z = row.cells()[1].value();
    const count = row.cells()[rowLength - 1].value();
    // x-axis
    if (!usedXs[x]) {
      xAxis.push(x);
      usedXs[x] = [];
    }
    // one dimensions
    if (!Array.isArray(oneD[x])) {
      oneD[x] = [];
    }
    for (let i = 1; i < rowLength; i++) {
      oneD[x].push(row.cells()[i].value());
    }
    // two dimensions
    if (!twoD[x] || !Array.isArray(zValuesByX[x])) {
      twoD[x] = {};
      zValuesByX[x] = [];
    }
    twoD[x][z] = count;
    zValuesByX[x].push(z);
    // find max values
    if (parseInt(z, 10) > maxZ) {
      maxZ = parseInt(z, 10);
    }
    if (parseInt(count, 10) > maxCount) {
      maxCount = parseInt(count, 10);
    }
  });

  if (
    !(presentation === PIE && dimensions === 1) &&
    [DATE, DATETIME].includes(
      updatedDataTable.headings()[0].columnType().type(),
    )
  ) {
    if (getChartOption(CONFIG_FILL_MISSING_VALUES)) {
      // Add missing dates if user requests it
      xAxis = addMissingDates(xAxis);
    } else {
      // Remove NULLs if missingValue is disabled
      xAxis = xAxis.filter((x) => x);
    }
  }

  xAxis = getChartOption(CONFIG_REVERSE_X_AXIS) ? xAxis : xAxis.reverse();

  const borderOpacity = presentation === BARLINE ? 1 : 0.6;

  const colours = baseColours.map((c) => `rgba(${c}, ${DEFAULT_RGBA_ALPHA})`);
  const borders = baseColours.map((c) => `rgba(${c}, ${borderOpacity})`);
  const fontColours = colours;

  const yAxes = [];
  const yAxisType = getChartOption(CONFIG_LOGARITHMIC)
    ? 'logarithmic'
    : 'linear';

  // adjust y-axis ticks so that they don't overlap (especially when using logarithmic scale)
  const afterBuildTicks = (axis) => {
    const DISTANCE_BETWEEN_TICKS = getChartOption(CONFIG_LOGARITHMIC) ? 50 : 25;
    const noOfTicks = Math.round(axis.height / DISTANCE_BETWEEN_TICKS);
    if (axis.ticks.length < noOfTicks) {
      return; // no need to adjust
    }
    axis.ticks = axis.ticks.filter(
      (tick, i) =>
        i === 0 || // first tick
        i === axis.ticks.length - 1 || // last tick
        i % Math.round(axis.ticks.length / noOfTicks) === 0, // limit number of ticks
    );
  };

  const asPercentage = (dataPoints) => {
    const sum = dataPoints.map((d) => parseFloat(d)).reduce((a, b) => a + b, 0);
    return dataPoints.map((d) =>
      sum === 0 ? 0 : (parseFloat(d) * 100.0) / sum,
    );
  };

  const asCumulative = (dataPoints) => {
    // When cumulative is selected, we automatically sort the data in ascending order
    // (regardless of how the query is sorted) because this is the only way a cumulative
    // total makes sense. If the user reverses it, we need to reverse it back to calculate
    // the running total.
    const dps = (
      getChartOption(CONFIG_REVERSE_X_AXIS)
        ? dataPoints.slice().reverse()
        : dataPoints.slice()
    ).map((d) => (!d ? 0 : parseFloat(d)));

    // new running total array
    const runningTotal = [];
    for (let i = 0; i < dps.length; i++) {
      const toAdd = dps[i];
      runningTotal[i] = (i === 0 ? 0 : runningTotal[i - 1]) + toAdd;
    }

    // once calculated, we return the running total (or the reversed version).
    return getChartOption(CONFIG_REVERSE_X_AXIS)
      ? runningTotal.reverse()
      : runningTotal;
  };

  const buildOneD = () => {
    const twoYAxes =
      (getChartOption(CONFIG_2ND_Y_AXIS) && rowLength === 3) ||
      presentation === BARLINE;
    const labels = [];
    const columnNames = [];
    for (let i = 1; i < rowLength; i++) {
      const labelToShow = getChartOption(
        CONFIG_COLUMN_NAME(updatedDataTable.headings()[i]),
      );
      labels.push(labelToShow);
      columnNames.push(updatedDataTable.headings()[i].columnName().toString());
    }
    return labels.map((label, index) => {
      const multiColours = [];
      const multiBorders = [];
      const dataPoints = xAxis.map((x, dataPointIndex) => {
        const colorOverride = hexToRgb(getChartOption(CONFIG_COLOR(x)));
        if (colorOverride) {
          multiColours.push(colorOverride);
          multiBorders.push(colorOverride);
        } else {
          multiColours.push(colours[dataPointIndex % colours.length]);
          multiBorders.push(borders[dataPointIndex % borders.length]);
        }
        if (!oneD[x]) {
          return 0; // happens if we're filling in missing gaps in x-axis
        }
        return oneD[x][index];
      });

      const yAxisName = twoYAxes ? `y-axis${index}` : 'y-axis';
      if (index === 0 || twoYAxes) {
        const yAxisFormatter = cellFormatter(
          updatedDataTable,
          getChartOption,
          1 + index,
        );
        yAxes.push({
          id: yAxisName,
          type: yAxisType,
          gridLines: {
            display: index === 0,
          },
          position: index === 0 ? 'left' : 'right',
          title: {
            display: getChartOption(CONFIG_SHOW_Y_AXIS_LABEL),
            text: getAxisLabel(getChartOption, index, label),
            color: !twoYAxes
              ? null
              : getChartOption(CONFIG_COLOR(columnNames[index]))
              ? getChartOption(CONFIG_COLOR(columnNames[index]))
              : fontColours[index],
            padding: 10,
          },
          min: getChartOption(CONFIG_LOGARITHMIC)
            ? 0 // beginAtZero doesn't apply to logarithmic, so force min 0 instead
            : index === 0
            ? getChartOption(CONFIG_Y_AXIS_MIN) || undefined
            : getChartOption(CONFIG_2ND_Y_AXIS_MIN) || undefined,
          max:
            index > 0
              ? getChartOption(CONFIG_2ND_Y_AXIS_MAX) || undefined
              : getChartOption(CONFIG_Y_AXIS_MAX) || undefined,
          ticks: {
            beginAtZero: true, // ignored by logarithmic (https://www.chartjs.org/docs/latest/axes/cartesian/logarithmic.html)
            callback: (tick) => {
              const result = yAxisFormatter(tick);
              return result === 'NULL' ? '0' : result;
            },
          },
          afterBuildTicks,
        });
      }
      const isPie =
        dimensions === 1 &&
        presentation === PIE &&
        updatedDataTable.headings().length === 2;
      const isPercent = getChartOption(CONFIG_SHOW_PERCENT) && isPie;
      const colorOverride = hexToRgb(
        getChartOption(CONFIG_COLOR(columnNames[index])),
        index === 1 && presentation === BARLINE ? 1 : DEFAULT_RGBA_ALPHA,
      );

      return {
        extras: {
          // not for chartjs
          originalLabel: columnNames[index],
        },
        type:
          twoYAxes && index === 1 && presentation === BARLINE
            ? LINE
            : undefined,
        label: !label
          ? columnNames[index] + (isCumulative ? ' (cumulative)' : '')
          : label === columnNames[index]
          ? label.replace(/_/g, ' ') + (isCumulative ? ' (cumulative)' : '')
          : label.replace(/_/g, ' '),
        yAxisID: isPie ? undefined : yAxisName,
        data: isCumulative
          ? asCumulative(dataPoints)
          : isPercent
          ? asPercentage(dataPoints)
          : dataPoints,
        backgroundColor: isPie ? multiColours : colorOverride || colours[index],
        fill: labels.length === 1,
        borderColor: isPie ? '#fff' : colorOverride || borders[index],
        lineTension: 0.2,
        borderWidth:
          (presentation === LINE && labels.length !== 1) ||
          (presentation === BARLINE && index === 1)
            ? 3
            : 1,
        pointBorderWidth: 0,
      };
    });
  };
  const sortZValues = () => {
    // 1. create a pair for every comparison relationship that we know of (e.g. for every {a > b}, create a pair [a, b])
    let pairs = [];
    const allValues = [];
    for (const x in zValuesByX) {
      const values = zValuesByX[x];
      values.forEach((v) => allValues.push(v));
      for (let i = 0; i < values.length; ++i) {
        for (let j = i + 1; j < values.length; ++j) {
          pairs.push([values[i], values[j]]);
        }
      }
    }
    // 2. add items in right order, by identifying which item does not appear on the right of the pairs
    const uniqueValues = [...new Set(allValues)];
    const sortedValues = [];
    while (true) {
      // eslint-disable-next-line no-loop-func
      const idx = uniqueValues.findIndex((v) => !pairs.find((p) => p[1] === v));
      if (idx === -1) {
        // no values are *not* represented on the right (also happens when there is no consistent order)
        break;
      }
      const nextValue = uniqueValues.splice(idx, 1)[0]; // remove item from list
      sortedValues.push(nextValue); // add it to sorted list
      pairs = pairs.filter((p) => p[0] !== nextValue); // remove all pairs that have it on the right
    }
    uniqueValues.forEach((v) => sortedValues.push(v)); // any left over values, add them to the end of the sorted list
    return sortedValues;
  };
  const isStacked = dimensions === 2 && presentation === PIE;
  const isPercent = getChartOption(CONFIG_SHOW_PERCENT) && isStacked;

  const build2D = () => {
    const dim2Formatter = cellFormatter(updatedDataTable, getChartOption, 1);
    const yAxisFormatter = cellFormatter(updatedDataTable, getChartOption, 2);
    const labelToShow = getChartOption(
      CONFIG_COLUMN_NAME(updatedDataTable.headings()[2]),
    );
    yAxes.push({
      type: yAxisType,
      scaleLabel: {
        display: getChartOption(CONFIG_SHOW_Y_AXIS_LABEL),
        labelString: getAxisLabel(getChartOption, 0, labelToShow),
        padding: 10,
        fontSize: 13,
      },
      stacked: isStacked,
      min: getChartOption(CONFIG_LOGARITHMIC)
        ? 0 // beginAtZero doesn't apply to logarithmic, so force min 0 instead
        : getChartOption(CONFIG_Y_AXIS_MIN) || (isPercent ? 0 : undefined),
      max: getChartOption(CONFIG_Y_AXIS_MAX) || (isPercent ? 100 : undefined),
      ticks: {
        beginAtZero: true,
        callback: (tick) => {
          return yAxisFormatter(tick);
        },
      },
      afterBuildTicks,
    });
    const sortedZAxis = sortZValues();
    const val = (z, x) => {
      if (!twoD[x]) {
        return 0; // happens if we're filling in missing gaps in x-axis
      }
      return parseFloat(twoD[x][z]) || 0;
    };
    const sums = xAxis.map((x) => {
      let sum = 0;
      sortedZAxis.map((z) => (sum += val(z, x)));
      return sum;
    });
    return sortedZAxis.map((z, index) => {
      const dataPoints = xAxis.map((x, i) => {
        return isPercent ? (val(z, x) * 100.0) / sums[i] : val(z, x);
      });
      const colorOverride = hexToRgb(getChartOption(CONFIG_COLOR(z)));
      return {
        extras: {
          // not used by chartjs
          originalLabel: z,
        },
        label: dim2Formatter(z),
        data: dataPoints, // [12, 7, 3, 5, 2, 3],
        backgroundColor: colorOverride || colours[index % colours.length],
        fill: false,
        lineTension: 0.2,
        borderColor: colorOverride || borders[index % colours.length],
        borderWidth: presentation === LINE ? 3 : 1,
        pointBorderWidth: 0,
      };
    });
  };

  const datasets = dimensions === 1 ? buildOneD() : build2D();

  return { xAxis, yAxes, datasets };
}

// NOTE: this is needed to make addMissingDates work in US timezones
function stringToDate(str) {
  const day = new Date(str);
  const tzOffset = day.getTimezoneOffset() * 60 * 1000; // apply offset when comparing dates to each other
  return new Date(day.getTime() + tzOffset);
}

const QUARTER_MONTHS = [1, 4, 7, 10];
const DAYS = 1000 * 60 * 60 * 24;
const WEEKS = 7 * DAYS;

export const deriveDateFormat = (dateString) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return 'DATE';

  /**
   * Supported formats:
   * - 2023-12-11 00:00:00.000
   * - 2023-12-11 00:00:00.000Z
   * - 2023-12-11 00:00:00.000+01:23
   * - 2023-12-11 00:00:00.000-01:23
   */
  if (
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}(Z|([\+-]\d{2}:\d{2}))?$/.test(
      dateString,
    )
  ) {
    return 'DATETIME';
  }

  return 'UNKNOWN_DATE_FORMAT';
};

const FORMAT_FN_MAP = {
  DATE_DATETIME: (dateString) => `${dateString} 00:00:00.000+00:00`,
  DATETIME_DATE: (dateString) => dateString.substring(0, 10),
};
export const formatDate = (from, to, dateString) => {
  if (from === to) return dateString;

  if (from === 'UNKNOWN_DATE_FORMAT' || to === 'UNKNOWN_DATE_FORMAT') {
    return dateString;
  }

  return FORMAT_FN_MAP[`${from}_${to}`](dateString);
};

export function addMissingDates(xAxis) {
  const firstDate = xAxis.find(Boolean);
  if (!firstDate) return xAxis;

  const initialDateFormat = deriveDateFormat(firstDate);

  const diffs = [];
  let max = null;
  let min = null;
  let hasNull = false;
  let prev = null;

  const listOfDaysAndMonths = [];
  for (let i = 0; i < xAxis.length; i++) {
    if (!xAxis[i]) {
      hasNull = true;
      continue;
    }
    const date = stringToDate(xAxis[i]);
    if (prev) {
      diffs.push(Math.abs(prev - stringToDate(xAxis[i])));
    }
    if (!max || date > max) {
      max = new Date(date);
    }
    if (!min || date < min) {
      min = new Date(date);
    }
    prev = date;
    listOfDaysAndMonths.push({
      day: date.getDate(),
      month: date.getMonth() + 1,
    });
  }

  const allDaysAre1st = listOfDaysAndMonths.every((x) => x.day === 1);

  const isYearInterval =
    allDaysAre1st && listOfDaysAndMonths.every((x) => x.month === 1);
  const isQuarterInterval =
    allDaysAre1st &&
    listOfDaysAndMonths.every((x) => QUARTER_MONTHS.includes(x.month));
  const isMonthInterval = allDaysAre1st;

  let addOne = null;
  if (isYearInterval) {
    addOne = (date) => date.setYear(1900 + date.getYear() + 1);
  } else if (isQuarterInterval) {
    addOne = (date) => date.setMonth(date.getMonth() + 3);
  } else if (isMonthInterval) {
    addOne = (date) => date.setMonth(date.getMonth() + 1);
  } else if (diffs.every((x) => x % WEEKS === 0)) {
    addOne = (date) => date.setDate(date.getDate() + 7);
  } else if (diffs.every((x) => x % DAYS === 0)) {
    addOne = (date) => date.setDate(date.getDate() + 1);
  } else {
    // dunno (so don't change anything)
    return xAxis;
  }
  const results = [];
  for (let date = min; date <= max; ) {
    const tzOffset = date.getTimezoneOffset() * 60 * 1000; // ignore offset when converting to ISOString
    const dateStr = new Date(date.getTime() - tzOffset)
      .toISOString()
      .substring(0, 10);

    const dateFormat = deriveDateFormat(dateStr);

    results.push(formatDate(dateFormat, initialDateFormat, dateStr));
    addOne(date);
  }
  if (hasNull) {
    results.push(null);
  }
  return results.reverse();
}

function collapseLongTailIfNecessary(
  dataTable,
  dimensions,
  presentation,
  getChartOption,
) {
  //
  // if more than N chart labels, collapse long tail into "OTHER"
  //
  const N =
    getChartOption(CONFIG_MAX_LEGEND_ITEMS) ||
    (presentation === PIE ||
    (dimensions === 2 && [BAR, LINE].includes(presentation))
      ? 12 // always limit legend size by default, as otherwise looks unruly
      : null);
  if (N && dataTable.rows().length > N) {
    // when 1 dimension, only PIE charts are affected (as all other charts have just one label)
    if (dimensions === 1 && dataTable.headings().length === 2) {
      // take top N rows
      const rows = dataTable.rows().slice();
      if (presentation === PIE) {
        // in pie we just want the top items (whereas for other charts we want the first items)
        rows.sort((a, b) => b.cells()[1].value() - a.cells()[1].value());
      }
      const shortTail = rows.slice(0, N);
      const longTail = rows
        .slice(N)
        .map((obj) => toFloat(obj.cells()[1].value()))
        .reduce((total, num) => total + num);
      const newRows =
        presentation !== PIE
          ? shortTail
          : [
              // for pie charts we collapse the long tail into OTHER
              ...shortTail.reverse(),
              new DataTableRow(-100, [
                new DataTableCell(OTHER_LABEL),
                new DataTableCell(longTail),
              ]),
            ];
      return new DataTable(dataTable.headings(), newRows);
    }
    if (
      dimensions === 2 &&
      dataTable.headings().length === 3 &&
      [BAR, PIE, LINE].includes(presentation)
    ) {
      // when 2 dimensions, the second dimension is the one that represents the label
      const totals = {}; // the total sum of each label
      dataTable.rows().map((row) => {
        // Get the total for each chart key so we can find the top N.
        // 	(we do this for the results as a whole, and not for each value in
        //  column A, because we need to end up with N chart keys)
        const key = row.cells()[1].value();
        const value = toFloat(row.cells()[2].value());
        totals[key] = (totals[key] || 0) + value;
      });
      const topN = {}; // the top N labels
      Object.entries(totals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, N)
        .map((entry) => (topN[entry[0]] = true));
      const others = {}; // the OTHER row counts
      const newRows = [];
      dataTable.rows().map((row) => {
        const cells = row.cells();
        const value = toFloat(row.cells()[2].value());
        if (!topN[cells[1].value()]) {
          // long tail
          others[cells[0].value()] = (others[cells[0].value()] || 0) + value;
        } else {
          newRows.push(row);
        }
      });
      // append long tail as rows to the end
      Object.entries(others).map((entry, i) => {
        let insertIndex = newRows.findIndex(
          (r) => r.cells()[0].value() === entry[0],
        );
        if (insertIndex < 0) {
          insertIndex = 0;
        }
        const rowId = -100 * (i + 1);
        newRows.splice(
          insertIndex,
          0,
          new DataTableRow(rowId, [
            new DataTableCell(entry[0] === 'null' ? null : entry[0]),
            new DataTableCell(OTHER_LABEL),
            new DataTableCell(entry[1]),
          ]),
        );
      });
      // if x axis is DATE, sort rows, as otherwise all OTHERs will just be appended on end
      if (
        [DATE, DATETIME].includes(dataTable.headings()[0].columnType().type())
      ) {
        newRows.sort((x, y) => {
          const a = x.cells()[0].value();
          const b = y.cells()[0].value();
          return a === b ? 0 : !b ? 1 : !a ? -1 : b.localeCompare(a);
        });
      }

      return new DataTable(dataTable.headings(), newRows);
    }
    if (
      dimensions === 1 &&
      dataTable.headings().length > 2 &&
      [BAR, LINE, BARLINE].includes(presentation)
    ) {
      // it's a multi metric chart, so restrict the rows to the first N
      const newRows = dataTable.rows().slice(0, N);
      return new DataTable(dataTable.headings(), newRows);
    }
  }
  return dataTable;
}

function toFloat(value) {
  if (!value) {
    return 0; // handle nulls
  }
  return parseFloat(value);
}

export function secondColIsDimension(
  headings,
  getChartOption,
  presentation = null,
) {
  if (headings.length !== 3) {
    return false;
  }
  if (presentation === PIE) {
    // PIE means 'stacked', so second col *must* be a dimension
    return true;
  }
  if (![INT, FLOAT].includes(headings[1].columnType().type())) {
    // can't be a metric, so must be a dimension
    return true;
  }
  // if(getChartOption(CONFIG_2ND_COL_IS_DIM) !== null) {
  // 	//user overriding value
  // 	return getChartOption(CONFIG_2ND_COL_IS_DIM);
  // }
  return headings[1].isDimension();
}

/**
 * flips first and second column of a 3 column data table
 */
export function flipDimsIfToggled(dataTable, getChartOption, presentation) {
  const dimensions = secondColIsDimension(
    dataTable.headings(),
    getChartOption,
    presentation,
  )
    ? 2
    : 1;
  if (dimensions === 2 && getChartOption(CONFIG_FLIP_DIMS)) {
    const headings = [
      dataTable.headings()[1],
      dataTable.headings()[0],
      dataTable.headings()[2],
    ];
    const rows = []; // list of DataTableRow objs
    dataTable.rows().map((r) => {
      const cells = [r.cells()[1], r.cells()[0], r.cells()[2]];
      rows.push(new DataTableRow(r.rowId(), cells));
    });
    return new DataTable(headings, rows);
  }

  return dataTable;
}

function getAxisLabel(getChartOption, index, labelToShow) {
  const labelToGet =
    index === 0 ? CONFIG_Y_AXIS_LABEL : CONFIG_2ND_Y_AXIS_LABEL;
  const newLabel = getChartOption(labelToGet);
  if (!newLabel) {
    return labelToShow.replace(/_/g, ' ');
  }
  return newLabel;
}

export function comparePeriodsIfToggled(dataTable) {
  const rows = dataTable.rows().slice(); // an array of dataTableRows
  // create new rows with an additional cell based on what's being compared.
  const agg = dataTable.headings()[0].aggregation();

  // remove null values and empty strings*i.e. nulls when they come back from S3 (they don't make sense if you're comparing periods as they don't belong to a period)
  let newRows = rows.filter(
    (row) => row.cells()[0].value() && row.cells()[0].value() !== '',
  );

  const newRow = (row, i, aggregation) => {
    const cells = row.cells();
    // get date value from first col and standardise format as yyyy-mm-dd, just incase it comes back in some other format

    const dateValue = ISO_DATE_OPTION.formatting(row.cells()[0].value());
    // extract the day, or the month (and format if necessary)
    const firstCell =
      aggregation === DAY
        ? new DataTableCell(dateValue.slice(-2))
        : new DataTableCell(
            MONTH_YEAR_OPTION.formatting(dateValue).substring(0, 3),
          );
    // extract the month, or the year, and format
    const secondCell =
      aggregation === DAY
        ? new DataTableCell(MONTH_YEAR_OPTION.formatting(dateValue))
        : new DataTableCell(JUST_YEAR_OPTION.formatting(dateValue));
    return new DataTableRow(i, [firstCell, secondCell, cells[1]]);
  };

  if (agg === DAY) {
    newRows = newRows
      .map((row, i) => {
        return newRow(row, i, DAY);
      })
      .sort((a, b) => {
        if (b.cells()[0].value() < a.cells()[0].value()) {
          return -1;
        }
        return 1;
      })
      .map((row, i) => new DataTableRow(i, row.cells()));
  }

  if (agg === MONTH) {
    newRows = newRows
      .sort((a, b) => {
        // sort the rows first so that the months appear from Jan-Dec along the bottom
        const month = (row) => {
          const dateValue = row.cells()[0].value();
          // standardise date format as yyyy-mm-dd, just incase it comes back in some other format
          const isoDate = ISO_DATE_OPTION.formatting(dateValue);
          return parseInt(isoDate.substring(5, 7), 10);
        };
        if (month(b) < month(a)) {
          return -1;
        }
        return 1;
      })
      .map((row, i) => {
        return newRow(row, i, MONTH);
      });
  }

  // create new headings
  const dateColAlias = dataTable.headings()[0].columnName().alias();
  const newHeading1 = new DataTableHeading(
    new ColumnName(dateColAlias, agg === DAY ? 'day_of_month' : 'month'),
    new ColumnTypeData('STRING'),
  );
  const newHeading2 = new DataTableHeading(
    new ColumnName(dateColAlias, agg === DAY ? 'month' : 'year'),
    new ColumnTypeData('STRING'),
    null,
    true,
  );
  const newHeadings = [newHeading1, newHeading2, dataTable.headings()[1]];

  // create new datatable
  return new DataTable(
    newHeadings,
    newRows,
    dataTable.aliases(),
    dataTable.drilldownOptions(),
  );
}

export function transformDatatableIfNecessary(
  dataTable,
  getChartOption,
  presentation,
) {
  if (!dataTable) {
    return;
  }
  if (
    getChartOption(CONFIG_COMPARE_PERIODS) &&
    ChartHolder.canComparePeriods(dataTable, presentation)
  ) {
    return comparePeriodsIfToggled(dataTable);
  }
  return flipDimsIfToggled(dataTable, getChartOption, presentation);
}
